export interface AffiliateProfile {
  id: string;
  name: string;
  email: string;
  phone?: string;
  role: string;
  referralCode: string;
  referralLink: string;
  createdAt: string;
  payoutMethod?: "bank" | "upi" | "paypal";
  payoutDetails?: {
    accountName?: string;
    accountNumber?: string;
    bankName?: string;
    ifscOrRouting?: string;
    swiftOrIban?: string;
    upiId?: string;
    paypalEmail?: string;
  };
}

export type CommissionStatus = "pending" | "approved" | "payable" | "paid" | "reversed";

export interface ReferralTransaction {
  id: string;
  businessName: string;
  outletCount: number;
  paymentAmount: number;
  currency: "INR" | "USD";
  commissionRate: number; // 0.05 (5%)
  commissionAmount: number;
  status: CommissionStatus;
  date: string;
  invoiceRef: string;
  notes?: string;
}

export interface AffiliateStats {
  clicks: number;
  leads: number;
  conversions: number;
  totalEarned: number;
  pending: number;
  approved: number;
  payable: number;
  paid: number;
  reversed: number;
}

const STORAGE_KEY_PROFILE = "tk_affiliate_profile_v1";
const STORAGE_KEY_STATS = "tk_affiliate_stats_v1";
const STORAGE_KEY_TRANSACTIONS = "tk_affiliate_txns_v1";
const STORAGE_KEY_REFERRAL_CODE = "tk_active_ref_code";

// Generate unique referral code from name or random
export function generateReferralCode(name: string): string {
  const clean = name.replace(/[^a-zA-Z]/g, "").toUpperCase().slice(0, 4) || "PARTNER";
  const num = Math.floor(100 + Math.random() * 900);
  return `TK-${clean}${num}`;
}

export function buildReferralLink(code: string): string {
  if (typeof window !== "undefined") {
    return `${window.location.origin}/?ref=${encodeURIComponent(code)}`;
  }
  return `https://www.thatkorner.com/?ref=${encodeURIComponent(code)}`;
}

export function getStoredAffiliate(): AffiliateProfile | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY_PROFILE);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function saveAffiliate(profile: AffiliateProfile): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(STORAGE_KEY_PROFILE, JSON.stringify(profile));
  } catch (e) {
    console.error("Failed to save affiliate profile", e);
  }
}

export function clearStoredAffiliate(): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.removeItem(STORAGE_KEY_PROFILE);
  } catch (e) {
    console.error("Failed to clear affiliate profile", e);
  }
}

export function getStoredTransactions(): ReferralTransaction[] {
  if (typeof window === "undefined") return DEFAULT_TRANSACTIONS;
  try {
    const raw = localStorage.getItem(STORAGE_KEY_TRANSACTIONS);
    if (!raw) {
      localStorage.setItem(STORAGE_KEY_TRANSACTIONS, JSON.stringify(DEFAULT_TRANSACTIONS));
      return DEFAULT_TRANSACTIONS;
    }
    return JSON.parse(raw);
  } catch {
    return DEFAULT_TRANSACTIONS;
  }
}

export function saveTransactions(txns: ReferralTransaction[]): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(STORAGE_KEY_TRANSACTIONS, JSON.stringify(txns));
  } catch (e) {
    console.error("Failed to save transactions", e);
  }
}

export function getAffiliateStats(): AffiliateStats {
  const txns = getStoredTransactions();
  let totalEarned = 0;
  let pending = 0;
  let approved = 0;
  let payable = 0;
  let paid = 0;
  let reversed = 0;
  let conversions = 0;

  for (const t of txns) {
    if (t.status !== "reversed") {
      totalEarned += t.commissionAmount;
      conversions += 1;
    }
    if (t.status === "pending") pending += t.commissionAmount;
    if (t.status === "approved") approved += t.commissionAmount;
    if (t.status === "payable") payable += t.commissionAmount;
    if (t.status === "paid") paid += t.commissionAmount;
    if (t.status === "reversed") reversed += t.commissionAmount;
  }

  let clicks = 48;
  let leads = 7;
  if (typeof window !== "undefined") {
    try {
      const rawStats = localStorage.getItem(STORAGE_KEY_STATS);
      if (rawStats) {
        const parsed = JSON.parse(rawStats);
        clicks = parsed.clicks ?? clicks;
        leads = parsed.leads ?? leads;
      }
    } catch {
      // ignore
    }
  }

  return {
    clicks,
    leads,
    conversions,
    totalEarned,
    pending,
    approved,
    payable,
    paid,
    reversed,
  };
}

// Active referral attribution for visitors
export function captureReferralCodeFromUrl(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref") || params.get("aff") || params.get("affiliate");
    if (ref) {
      const sanitized = ref.trim().toUpperCase();
      localStorage.setItem(STORAGE_KEY_REFERRAL_CODE, sanitized);
      // Set 90-day attribution cookie
      document.cookie = `tk_ref=${encodeURIComponent(sanitized)}; max-age=7776000; path=/; SameSite=Lax`;
      return sanitized;
    }
  } catch {
    // ignore
  }
  return null;
}

export function getActiveReferralCode(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const local = localStorage.getItem(STORAGE_KEY_REFERRAL_CODE);
    if (local) return local;
    const match = document.cookie.match(/(?:^|;\s*)tk_ref=([^;]+)/);
    if (match && match[1]) {
      return decodeURIComponent(match[1]);
    }
  } catch {
    // ignore
  }
  return null;
}

export const DEFAULT_TRANSACTIONS: ReferralTransaction[] = [
  {
    id: "TXN-701",
    businessName: "L'Aura Bistro & Lounge",
    outletCount: 2,
    paymentAmount: 50000,
    currency: "INR",
    commissionRate: 0.05,
    commissionAmount: 2500,
    status: "paid",
    date: "14 Oct 2026",
    invoiceRef: "INV-2026-0891",
    notes: "First eligible annual subscription payment collected.",
  },
  {
    id: "TXN-702",
    businessName: "The Spice Pavilion",
    outletCount: 1,
    paymentAmount: 20000,
    currency: "INR",
    commissionRate: 0.05,
    commissionAmount: 1000,
    status: "payable",
    date: "02 Oct 2026",
    invoiceRef: "INV-2026-0844",
    notes: "First eligible onboarding payment verified.",
  },
  {
    id: "TXN-703",
    businessName: "Saffron Coastal Dining",
    outletCount: 3,
    paymentAmount: 75000,
    currency: "INR",
    commissionRate: 0.05,
    commissionAmount: 3750,
    status: "approved",
    date: "28 Sep 2026",
    invoiceRef: "INV-2026-0812",
    notes: "Review period cleared. Moving to payable next payout cycle.",
  },
  {
    id: "TXN-704",
    businessName: "Café Miel Boutique",
    outletCount: 1,
    paymentAmount: 20000,
    currency: "INR",
    commissionRate: 0.05,
    commissionAmount: 1000,
    status: "pending",
    date: "12 Oct 2026",
    invoiceRef: "INV-2026-0922",
    notes: "Under 14-day payment settlement verification.",
  },
];
